/**
 * Removes any leftover Home Screen layout autosave drafts from localStorage.
 *
 * Background
 *   Earlier builds of the editor auto-persisted a copy of the in-progress
 *   layout under the `homeScreenLayout:draft:*` prefix. We removed that
 *   behavior in favor of an explicit unsaved-changes prompt managed by the
 *   parent ConfigurationWizard, but we keep this purge so admins who used
 *   the old behavior don't get stale arrangements silently re-hydrated when
 *   they open the editor today.
 *
 * Notes
 *   - SSR-safe: returns early if `window` is undefined.
 *   - Best-effort: any localStorage error (private mode, quota) is swallowed
 *     so a startup hiccup never blocks the editor from rendering.
 *   - Returns the count of removed keys so callers (and tests) can assert
 *     the cleanup happened without re-reading localStorage.
 */
export const LEGACY_STORAGE_PREFIX = "homeScreenLayout:draft:";

export function purgeLegacyDrafts(): number {
  if (typeof window === "undefined") return 0;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(LEGACY_STORAGE_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) window.localStorage.removeItem(k);
    return toRemove.length;
  } catch {
    /* private mode / quota — best effort cleanup, never block the UI */
    return 0;
  }
}
